import Clear from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import { LoadingButton } from "@mui/lab";
import {
  Autocomplete,
  CircularProgress,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import { FormDatePicker, FormTextField } from "components/atoms";
import { DetailTableTabs, ReportHeader } from "components/molecules";
import { FormikHelpers, useField, useFormik } from "formik";
import { TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import { isEmpty } from "lodash";
import {
  ICompany,
  IDeliveryBarcodeHistory,
  IDeliveryBarcodeHistoryDetail,
  IDownloadDeliveryHistoryReportRequest,
  IDownloadExcelRequest,
  IGetDeliveryBarcodeHistoryDetailRequest,
  IGetDeliveryBarcodeHistoryRequest,
  IPaginateApiResponse,
  IPaginationRequest,
} from "model";
import moment from "moment";
import React, { useState } from "react";
import { DownloadActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { MasterDataApiService, ReportApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import { FinishProductList, QRCodeList } from "./components";

export interface IDeliveryDetailParams {
  fromDate: string;
  toDate: string;
  companyCode: string;
  searchText: string;
}

interface IDeliveryHistoryContext {
  handleProductListChangePage(pageNumber: number): void;
  handleProductListPageSizeChange(pageSize: number): void;
  handleQRCodeListChangePage(pageNumber: number): void;
  handleCodeListPageSizeChange(pageSize: number): void;
  handleSearchProductList(searchText: string): void;
  handleSearchQRCodeList(searchText: string): void;
}

const DeliveryHistoryContext = React.createContext<IDeliveryHistoryContext>({
  handleCodeListPageSizeChange: () => {},
  handleProductListPageSizeChange: () => {},
  handleQRCodeListChangePage: () => {},
  handleProductListChangePage: () => {},
  handleSearchProductList: () => {},
  handleSearchQRCodeList: () => {},
});
export const useDeliveryHistoryContext = () => {
  return React.useContext(DeliveryHistoryContext);
};

const DeliveryDetailReport = () => {
  const dispatch = useAppDispatch();
  const [request, setRequest] = useState<
    IGetDeliveryBarcodeHistoryRequest | IGetDeliveryBarcodeHistoryDetailRequest
  >({
    companyCode: "",
    fromDate: TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: TimeHelper.endOfWeek("MM-DD-YYYY"),
  });
  const [productListRequest, setRequestListRequest] = useState({
    ...request,
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
  });
  const [qrListRequest, setQRListRequest] = useState({
    ...request,
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
  });
  const handleSubmit = (values: IDeliveryDetailParams) => {
    setRequest({
      searchText: values.searchText,
      companyCode: values.companyCode,
      fromDate: values.fromDate,
      toDate: values.toDate,
    });
    setRequestListRequest({ ...productListRequest, ...values });
    setQRListRequest({ ...qrListRequest, ...values });
  };

  const { loading: productListLoading, result: productListResult } =
    usePaginationRequest<
      IGetDeliveryBarcodeHistoryRequest,
      IDeliveryBarcodeHistory[]
    >(productListRequest, ReportApiService.getDeliveryBarcodeHistoryList, [
      request,
    ]);

  const handleProductListChangePage = (pageNumber: number) => {
    setRequestListRequest({
      ...productListRequest,
      pageNumber: pageNumber,
    });
  };
  const handleProductListPageSizeChange = (pageSize: number) => {
    setRequestListRequest({ ...productListRequest, pageSize: pageSize });
  };

  const { loading: qrCodeListLoading, result: qrCodeListResult } =
    usePaginationRequest<
      IGetDeliveryBarcodeHistoryDetailRequest,
      IDeliveryBarcodeHistoryDetail[]
    >(qrListRequest, ReportApiService.getDeliveryBarcodeHistoryDetail, [
      request,
    ]);

  const handleQRCodeListChangePage = (pageNumber: number) => {
    setQRListRequest({
      ...qrListRequest,
      pageNumber: pageNumber,
    });
  };
  const handleCodeListPageSizeChange = (pageSize: number) => {
    setQRListRequest({ ...qrListRequest, pageSize: pageSize });
  };

  const handleSearchProductList = (searchText: string) => {
    setRequest({ ...request, searchText });

    setRequestListRequest({ ...productListRequest, searchText });
  };
  const handleSearchQRCodeList = (searchText: string) => {
    setRequest({ ...request, searchText });

    setQRListRequest({ ...qrListRequest, searchText });
  };

  const onDownloadDeliveryHistoryReport = async () => {
    const params: IDownloadDeliveryHistoryReportRequest = {
      ...request,
      companyCode: request.companyCode,
      fromDate: request.fromDate,
      toDate: request.toDate,
      fileName: EXPORT_FILE_NAME.deliveryReport,
      pageSize: (
        productListResult as IPaginateApiResponse<IDeliveryBarcodeHistory[]>
      ).totalRecords,
      pageNumber: FIRST_PAGE,
    };
    dispatch(DownloadActions.downloadDeliveryHistoryReport.request(params));
  };

  const onDownloadDeliveryMarketingReport = async () => {
    const params: IDownloadExcelRequest = {
      ...request,
      fromDate: request.fromDate,
      toDate: request.toDate,
      fileName: EXPORT_FILE_NAME.deliveryForMarketingReport,
      pageSize: (
        productListResult as IPaginateApiResponse<IDeliveryBarcodeHistory[]>
      ).totalRecords,
      pageNumber: FIRST_PAGE,
    };
    dispatch(
      DownloadActions.downloadDeliveryHistoryForMarketingReport.request(params)
    );
  };

  const value = React.useMemo(
    () =>
      ({
        handleCodeListPageSizeChange,
        handleProductListChangePage,
        handleProductListPageSizeChange,
        handleQRCodeListChangePage,
        handleSearchProductList,
        handleSearchQRCodeList,
      } as IDeliveryHistoryContext),
    []
  );

  return (
    <DeliveryHistoryContext.Provider value={value}>
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          height: "100%",
        }}
      >
        <DetailHeader
          request={productListRequest}
          onSubmit={handleSubmit}
          onDownloadDeliveryHistoryReport={onDownloadDeliveryHistoryReport}
          onDownloadDeliveryMarketingReport={onDownloadDeliveryMarketingReport}
        />
        <TabTablesSection
          request={request}
          productListLoading={productListLoading}
          qrCodeListLoading={qrCodeListLoading}
          productListResult={productListResult}
          qrCodeListResult={qrCodeListResult}
        />
      </div>
    </DeliveryHistoryContext.Provider>
  );
};

const DetailHeader = (props: {
  request: IGetDeliveryBarcodeHistoryDetailRequest;
  onSubmit(values: IDeliveryDetailParams): void;
  onDownloadDeliveryMarketingReport(): void;
  onDownloadDeliveryHistoryReport(): void;
}) => {
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const {
    request,
    onSubmit,
    onDownloadDeliveryMarketingReport,
    onDownloadDeliveryHistoryReport,
  } = props;
  const formik = useFormik<any>({
    initialValues: {
      company: null,
      companyCode: "",
      searchText: "",
      fromDate: moment(request.fromDate, "MM-DD-YYYY"),
      toDate: moment(request.toDate, "MM-DD-YYYY"),
    },
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      onSubmit({
        searchText: values.searchText,
        companyCode: values.company ? values.company.companyCode : "",
        fromDate: TimeHelper.formatMomentToDate(values.fromDate, "MM-DD-YYYY"),
        toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
      });
    },
  });

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.deliveryDetail)}
      formik={formik}
      loading={false}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <LoadingButton
            loading={downloading}
            onClick={onDownloadDeliveryMarketingReport}
            startIcon={<DownloadIcon />}
            variant="contained"
          >
            {I18nTranslator(translations.button.exportMarketingDocs)}
          </LoadingButton>
          <LoadingButton
            loading={downloading}
            onClick={onDownloadDeliveryHistoryReport}
            startIcon={<DownloadIcon />}
            variant="contained"
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={3} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
            defaultValue={TimeHelper.formatDate(formik.values.deliveryDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
            defaultValue={TimeHelper.formatDate(formik.values.deliveryDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <CompanyAutoComplete id={"company"} label={"Khách hàng"} />
        </Grid>
        <Grid xs={3} item>
          <FormTextField
            label={I18nTranslator(translations.search)}
            placeholder={"Tìm kiếm"}
            id="searchText"
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

const CompanyAutoComplete = (props: { id: string; label: string }) => {
  const { id, label } = props;
  const [_, meta, helper] = useField(id);

  const [open, setOpen] = React.useState(false);
  const [request] = useState({
    pageNumber: 1,
    pageSize: 100000,
  });
  const { loading, result } = usePaginationRequest<
    IPaginationRequest,
    ICompany[]
  >(request, MasterDataApiService.getAllCompanies);

  return (
    <Autocomplete
      id={id}
      sx={{ width: "100%" }}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      isOptionEqualToValue={(option, value) =>
        option.companyCode === value.companyCode
      }
      getOptionLabel={(option) =>
        `${option.companyCode} - ${option.companyName}`
      }
      options={result.data || []}
      loading={true}
      value={meta.value}
      onChange={(_, value) => {
        if (value) {
          helper.setValue(value);
        }
      }}
      clearIcon={
        <Clear
          onClick={() => {
            helper.setValue(null);
          }}
        />
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={id}
          label={label}
          fullWidth
          error={meta.touched && !isEmpty(meta.error)}
          helperText={meta.touched && meta.error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

const TabTablesSection = (props: {
  request: IGetDeliveryBarcodeHistoryDetailRequest;
  productListLoading: boolean;
  qrCodeListLoading: boolean;
  productListResult: IPaginateApiResponse<IDeliveryBarcodeHistory[]>;
  qrCodeListResult: IPaginateApiResponse<IDeliveryBarcodeHistoryDetail[]>;
}) => {
  const {
    request,
    productListResult,
    qrCodeListResult,
    qrCodeListLoading,
    productListLoading,
  } = props;
  const tabsName = [
    I18nTranslator(translations.report.finishProductList),
    I18nTranslator(translations.report.QRCodeList),
  ];

  const tabsPanel = [
    <FinishProductList
      request={request}
      result={productListResult}
      loading={productListLoading}
    />,
    <QRCodeList
      request={request}
      result={qrCodeListResult}
      loading={qrCodeListLoading}
    />,
  ];

  return (
    <DetailTableTabs
      hideHeaderButton
      headerButtons={[]}
      tabsName={tabsName}
      tabsPanel={tabsPanel}
    />
  );
};

export default DeliveryDetailReport;
